import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ObservationRecord } from '../../types/observations';
import {
  ALL_WEARABLE_METRIC_IDS,
  WEARABLE_METRIC_DEFINITIONS,
  type WearableMetricId,
} from '../../data/wearableMetrics';
import {
  buildWearableMetricDatasets,
  getWearableMetricDatasetSummary,
  type WearableMetricDataset,
} from '../../services/memberWearableMetricsService';
import WearableMetricChart from './WearableMetricChart';

const cardStyle: React.CSSProperties = {
  borderRadius: 28,
  padding: 24,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(245,243,240,0.98) 100%)',
  color: 'var(--melio-charcoal)',
  border: '1px solid var(--melio-border)',
  boxShadow: '0 20px 40px rgba(26, 26, 24, 0.06)',
};

const mutedStyle: React.CSSProperties = {
  color: 'var(--melio-text-muted)',
};

function TrendHeader({ eyebrow, value, delta }: { eyebrow: string; value: string; delta: string }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] mb-2" style={mutedStyle}>
          {eyebrow}
        </div>
        <div style={{ fontSize: '2.4rem', lineHeight: 1, fontWeight: 600 }}>{value}</div>
      </div>
      <div className="rounded-full px-3 py-1 text-sm" style={{ backgroundColor: 'rgba(79, 93, 24,0.14)', color: 'var(--melio-sage)' }}>
        {delta}
      </div>
    </div>
  );
}

interface MemberBiomarkerTrendGridProps {
  observations: ObservationRecord[];
  selectedMetricIds: WearableMetricId[];
  onSaveSelection: (metricIds: WearableMetricId[]) => Promise<void> | void;
  savingSelection?: boolean;
}

function WearableMetricCard({ dataset }: { dataset: WearableMetricDataset }) {
  return (
    <Link to={`/intelligence/wearables/${dataset.id}`} className="block transition-opacity hover:opacity-90">
      <div style={cardStyle}>
        <TrendHeader eyebrow={dataset.definition.label} value={dataset.latestValueDisplay} delta={dataset.deltaLabel} />
        <WearableMetricChart dataset={dataset} />
        <p className="text-sm mt-4" style={{ color: 'var(--melio-text-muted)' }}>
          {getWearableMetricDatasetSummary(dataset)}
        </p>
      </div>
    </Link>
  );
}

export default function MemberBiomarkerTrendGrid({
  observations,
  selectedMetricIds,
  onSaveSelection,
  savingSelection = false,
}: MemberBiomarkerTrendGridProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftSelection, setDraftSelection] = useState<WearableMetricId[]>(selectedMetricIds);

  useEffect(() => {
    setDraftSelection(selectedMetricIds);
  }, [selectedMetricIds]);

  const datasets = useMemo(() => buildWearableMetricDatasets(observations), [observations]);
  const selectedDatasets = selectedMetricIds.map((metricId) => datasets[metricId]).filter(Boolean);

  const toggleMetric = (metricId: WearableMetricId) => {
    setDraftSelection((current) => {
      if (current.includes(metricId)) {
        return current.filter((id) => id !== metricId);
      }
      if (current.length >= 4) {
        return current;
      }
      return [...current, metricId];
    });
  };

  const handleSave = async () => {
    if (draftSelection.length !== 4) return;
    await onSaveSelection(draftSelection);
    setIsEditing(false);
  };

  return (
    <section className="mb-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h2
            className="text-2xl font-normal"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Biomarker and wearable trends
          </h2>
          <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            Live WHOOP-backed signals over the recent wearable window
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsEditing((current) => !current)}
          className="inline-flex items-center justify-center px-4 py-2 rounded text-sm font-medium transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
        >
          {isEditing ? 'Close metric picker' : 'Edit dashboard metrics'}
        </button>
      </div>

      {isEditing && (
        <div
          className="rounded-3xl border p-6 md:p-8 mb-6 bg-white"
          style={{ borderColor: 'var(--melio-border)' }}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-5">
            <div>
              <h3 className="text-xl font-normal mb-2" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
                Choose the four metrics shown on your dashboard
              </h3>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                Pick any four from the WHOOP metrics Melio is currently tracking. Tap a chart later to open a deeper explanation page.
              </p>
            </div>
            <div className="text-sm" style={{ color: draftSelection.length === 4 ? 'var(--melio-sage)' : '#b45309' }}>
              {draftSelection.length}/4 selected
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
            {ALL_WEARABLE_METRIC_IDS.map((metricId) => {
              const selected = draftSelection.includes(metricId);
              const disabled = !selected && draftSelection.length >= 4;
              return (
                <button
                  key={metricId}
                  type="button"
                  onClick={() => toggleMetric(metricId)}
                  disabled={disabled}
                  className="text-left rounded-2xl border px-4 py-3 transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    borderColor: selected ? WEARABLE_METRIC_DEFINITIONS[metricId].accentColor : 'var(--melio-border)',
                    backgroundColor: selected ? 'rgba(79, 93, 24, 0.08)' : 'white',
                    color: 'var(--melio-charcoal)',
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{WEARABLE_METRIC_DEFINITIONS[metricId].label}</span>
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs"
                      style={{
                        backgroundColor: selected ? WEARABLE_METRIC_DEFINITIONS[metricId].accentColor : 'rgba(26, 26, 24,0.08)',
                        color: selected ? 'white' : 'var(--melio-text-muted)',
                      }}
                    >
                      {selected ? '✓' : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={savingSelection || draftSelection.length !== 4}
              className="inline-flex items-center justify-center px-4 py-2 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {savingSelection ? 'Saving...' : 'Save dashboard metrics'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraftSelection(selectedMetricIds);
                setIsEditing(false);
              }}
              className="inline-flex items-center justify-center px-4 py-2 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {observations.length === 0 ? (
        <div className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <h3 className="text-xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            No parsed wearable trends yet
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
            Upload and parse WHOOP data first, then your selected dashboard charts will populate here automatically.
          </p>
        </div>
      ) : (
        <div className="grid xl:grid-cols-2 gap-5">
          {selectedDatasets.map((dataset) => (
            <WearableMetricCard key={dataset.id} dataset={dataset} />
          ))}
        </div>
      )}
    </section>
  );
}
